import { Currency, CurrencyAmount, ETHER, JSBI, Token, TokenAmount } from '@pancakeswap/sdk'
import { useMemo } from 'react'
import useSWR from 'swr'
import { useWeb3React } from '@web3-react/core'
import useActiveWeb3React from 'hooks/useActiveWeb3React'
import { isRobinhoodChain } from 'config/constants/chains'
import ERC20_INTERFACE from 'config/abi/erc20'
import { useAllTokens } from 'hooks/Tokens'
import { useMulticallContract } from 'hooks/useContract'
import { isAddress } from 'utils'
import orderBy from 'lodash/orderBy'
import { useSingleContractMultipleData, useMultipleContractSingleData } from '../multicall/hooks'

/**
 * Returns a map of the given addresses to their eventually consistent BNB balances.
 */
export function useBNBBalances(uncheckedAddresses?: (string | undefined)[]): {
  [address: string]: CurrencyAmount | undefined
} {
  const multicallContract = useMulticallContract()
  const { library, chainId } = useActiveWeb3React()

  const addresses: string[] = useMemo(
    () =>
      uncheckedAddresses ? orderBy(uncheckedAddresses.map(isAddress).filter((a): a is string => a !== false)) : [],
    [uncheckedAddresses],
  )

  const results = useSingleContractMultipleData(
    isRobinhoodChain(chainId) ? undefined : multicallContract,
    'getEthBalance',
    addresses.map((address) => [address]),
  )

  const robinhoodKey =
    isRobinhoodChain(chainId) && addresses.length > 0
      ? ['robinhood-native-balances', ...addresses]
      : null

  const { data: robinhoodBalances } = useSWR(
    robinhoodKey,
    async () => {
      const entries = await Promise.all(
        addresses.map(async (address) => {
          const balance = await library.getBalance(address)
          return [address, CurrencyAmount.ether(JSBI.BigInt(balance.toString()))] as const
        }),
      )

      return entries.reduce<{ [address: string]: CurrencyAmount }>((memo, [address, balance]) => {
        memo[address] = balance
        return memo
      }, {})
    },
    {
      refreshInterval: 10000,
      dedupingInterval: 3000,
      revalidateOnFocus: true,
    },
  )

  return useMemo(() => {
    if (isRobinhoodChain(chainId)) {
      return robinhoodBalances ?? {}
    }

    return addresses.reduce<{ [address: string]: CurrencyAmount }>((memo, address, i) => {
      const value = results?.[i]?.result?.[0]
      if (value) memo[address] = CurrencyAmount.ether(JSBI.BigInt(value.toString()))
      return memo
    }, {})
  }, [addresses, chainId, results, robinhoodBalances])
}


function useRobinhoodTokenBalances(
  address?: string,
  tokens?: Token[],
): [{ [tokenAddress: string]: TokenAmount | undefined }, boolean] {
  const { library, chainId } = useActiveWeb3React()

  const tokenAddresses = useMemo(
    () => tokens?.map((token) => token.address) ?? [],
    [tokens],
  )

  const key =
    isRobinhoodChain(chainId) && address && tokenAddresses.length > 0
      ? ['robinhood-token-balances', address, ...tokenAddresses]
      : null

  const { data, error } = useSWR(
    key,
    async () => {
      const entries = await Promise.all(
        (tokens ?? []).map(async (token) => {
          const callData = ERC20_INTERFACE.encodeFunctionData('balanceOf', [address])
          const result = await library.call({
            to: token.address,
            data: callData,
          })

          const [rawBalance] = ERC20_INTERFACE.decodeFunctionResult('balanceOf', result)

          return [
            token.address,
            new TokenAmount(token, JSBI.BigInt(rawBalance.toString())),
          ] as const
        }),
      )

      return entries.reduce<{ [tokenAddress: string]: TokenAmount }>(
        (balances, [tokenAddress, balance]) => {
          balances[tokenAddress] = balance
          return balances
        },
        {},
      )
    },
    {
      refreshInterval: 10000,
      dedupingInterval: 3000,
      revalidateOnFocus: true,
    },
  )

  return [data ?? {}, Boolean(key && !data && !error)]
}

/**
 * Returns a map of token addresses to their eventually consistent token balances for a single account.
 */
export function useTokenBalancesWithLoadingIndicator(
  address?: string,
  tokens?: (Token | undefined)[],
): [{ [tokenAddress: string]: TokenAmount | undefined }, boolean] {
  const validatedTokens: Token[] = useMemo(
    () => tokens?.filter((t?: Token): t is Token => isAddress(t?.address) !== false) ?? [],
    [tokens],
  )

  const validatedTokenAddresses = useMemo(() => validatedTokens.map((vt) => vt.address), [validatedTokens])

  const balances = useMultipleContractSingleData(
    validatedTokenAddresses,
    ERC20_INTERFACE,
    'balanceOf',
    useMemo(() => [address], [address]),
  )

  const { chainId } = useActiveWeb3React()
  const [robinhoodBalances, robinhoodLoading] = useRobinhoodTokenBalances(address, validatedTokens)

  const multicallBalances = useMemo(
    () =>
      address && validatedTokens.length > 0
        ? validatedTokens.reduce<{ [tokenAddress: string]: TokenAmount | undefined }>((memo, token, i) => {
            const value = balances?.[i]?.result?.[0]
            const amount = value ? JSBI.BigInt(value.toString()) : undefined
            if (amount) {
              memo[token.address] = new TokenAmount(token, amount)
            }
            return memo
          }, {})
        : {},
    [address, validatedTokens, balances],
  )

  const anyLoading = useMemo(
    () => balances.some((callState) => callState.loading),
    [balances],
  )

  if (isRobinhoodChain(chainId)) {
    return [robinhoodBalances, robinhoodLoading]
  }

  return [multicallBalances, anyLoading]
}

export function useTokenBalances(
  address?: string,
  tokens?: (Token | undefined)[],
): { [tokenAddress: string]: TokenAmount | undefined } {
  return useTokenBalancesWithLoadingIndicator(address, tokens)[0]
}

// get the balance for a single token/account combo
export function useTokenBalance(account?: string, token?: Token): TokenAmount | undefined {
  const tokenBalances = useTokenBalances(account, [token])
  if (!token) return undefined
  return tokenBalances[token.address]
}

export function useCurrencyBalances(
  account?: string,
  currencies?: (Currency | undefined)[],
): (CurrencyAmount | undefined)[] {
  const tokens = useMemo(
    () => currencies?.filter((currency): currency is Token => currency instanceof Token) ?? [],
    [currencies],
  )

  const tokenBalances = useTokenBalances(account, tokens)
  const containsBNB: boolean = useMemo(() => currencies?.some((currency) => currency === ETHER) ?? false, [currencies])
  const bnbBalance = useBNBBalances(containsBNB ? [account] : [])

  return useMemo(
    () =>
      currencies?.map((currency) => {
        if (!account || !currency) return undefined
        if (currency instanceof Token) return tokenBalances[currency.address]
        if (currency === ETHER) return bnbBalance[account]
        return undefined
      }) ?? [],
    [account, currencies, bnbBalance, tokenBalances],
  )
}

export function useCurrencyBalance(account?: string, currency?: Currency): CurrencyAmount | undefined {
  return useCurrencyBalances(account, [currency])[0]
}

// mimics useAllBalances
export function useAllTokenBalances(): { [tokenAddress: string]: TokenAmount | undefined } {
  const { account } = useWeb3React()
  const allTokens = useAllTokens()
  const allTokensArray = useMemo(() => Object.values(allTokens ?? {}), [allTokens])
  const balances = useTokenBalances(account ?? undefined, allTokensArray)
  return balances ?? {}
}
